import { useMemo, useState } from "react";
import { weeklyRevenue } from "@/mocks/admin/stats";

type RangeKey = "week" | "month" | "year";

type RangeData = {
  label: string;
  helper: string;
  total: string;
  delta: string;
  compare: string;
  bars: { label: string; value: number }[];
};

const RANGES: Record<RangeKey, RangeData> = {
  week: {
    label: "Week",
    helper: "Daily completed move revenue",
    total: "$158,300",
    delta: "+14.8%",
    compare: "vs. previous week",
    bars: weeklyRevenue.map((d) => ({ label: d.day, value: d.value })),
  },
  month: {
    label: "Month",
    helper: "Weekly completed move revenue",
    total: "$642,800",
    delta: "+9.2%",
    compare: "vs. previous month",
    bars: [
      { label: "W1", value: 142500 },
      { label: "W2", value: 168200 },
      { label: "W3", value: 155400 },
      { label: "W4", value: 176700 },
    ],
  },
  year: {
    label: "Year",
    helper: "Monthly completed move revenue",
    total: "$7.84M",
    delta: "+22.1%",
    compare: "vs. previous year",
    bars: [
      { label: "Jan", value: 512000 },
      { label: "Feb", value: 488000 },
      { label: "Mar", value: 564000 },
      { label: "Apr", value: 612000 },
      { label: "May", value: 698000 },
      { label: "Jun", value: 742000 },
      { label: "Jul", value: 810000 },
      { label: "Aug", value: 786000 },
      { label: "Sep", value: 705000 },
      { label: "Oct", value: 668000 },
      { label: "Nov", value: 612000 },
      { label: "Dec", value: 645000 },
    ],
  },
};

function formatShort(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v}`;
}

function formatAxis(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return `$${v}`;
}

export default function RevenueChart() {
  const [range, setRange] = useState<RangeKey>("week");
  const data = RANGES[range];

  const { max, avg, peak, low, gridlines } = useMemo(() => {
    const values = data.bars.map((d) => d.value);
    const m = Math.max(...values);
    const lowVal = Math.min(...values);
    const sum = values.reduce((a, b) => a + b, 0);
    const a = Math.round(sum / values.length);
    const peakBar = data.bars.find((b) => b.value === m)!;
    const lowBar = data.bars.find((b) => b.value === lowVal)!;
    // build 4 gridline values: 0, 1/3, 2/3, max
    const lines = [m, Math.round(m * 0.66), Math.round(m * 0.33), 0];
    return { max: m, avg: a, peak: peakBar, low: lowBar, gridlines: lines };
  }, [data.bars]);

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-6">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="font-display text-lg font-bold text-ink-900">
            Revenue This {data.label}
          </h3>
          <p className="text-xs text-ink-500 mt-0.5">{data.helper}</p>
        </div>
        <div className="flex items-center gap-1 bg-cream-100 p-1 rounded-full">
          {(Object.keys(RANGES) as RangeKey[]).map((key) => {
            const active = key === range;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setRange(key)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  active
                    ? "bg-white text-ink-900"
                    : "text-ink-500 hover:text-ink-900"
                }`}
              >
                {RANGES[key].label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-baseline gap-3 mt-4 mb-5">
        <span className="font-display text-3xl font-bold text-ink-900">
          {data.total}
        </span>
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-ink-700">
          <i className="ri-arrow-up-line"></i>
          {data.delta}
        </span>
        <span className="text-xs text-ink-400">{data.compare}</span>
      </div>

      {/* Quick stat strip */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <div className="bg-cream-50 border border-ink-100 rounded-md px-3 py-2">
          <div className="flex items-center gap-1 text-[10px] font-semibold text-ink-500 uppercase tracking-wide">
            <i className="ri-arrow-up-circle-line text-ink-700"></i>
            Peak
          </div>
          <div className="mt-0.5 text-sm font-bold text-ink-900">
            {formatShort(peak.value)}
          </div>
          <div className="text-[11px] text-ink-500">{peak.label}</div>
        </div>
        <div className="bg-cream-50 border border-ink-100 rounded-md px-3 py-2">
          <div className="flex items-center gap-1 text-[10px] font-semibold text-ink-500 uppercase tracking-wide">
            <i className="ri-bar-chart-2-line text-ink-700"></i>
            Average
          </div>
          <div className="mt-0.5 text-sm font-bold text-ink-900">
            {formatShort(avg)}
          </div>
          <div className="text-[11px] text-ink-500">per {data.label.toLowerCase()}ly bar</div>
        </div>
        <div className="bg-cream-50 border border-ink-100 rounded-md px-3 py-2">
          <div className="flex items-center gap-1 text-[10px] font-semibold text-ink-500 uppercase tracking-wide">
            <i className="ri-arrow-down-circle-line text-ink-700"></i>
            Low
          </div>
          <div className="mt-0.5 text-sm font-bold text-ink-900">
            {formatShort(low.value)}
          </div>
          <div className="text-[11px] text-ink-500">{low.label}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex gap-3">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between h-48 py-1 text-[10px] font-medium text-ink-400 text-right pr-1 select-none">
          {gridlines.map((g, i) => (
            <span key={i}>{formatAxis(g)}</span>
          ))}
        </div>

        {/* Bars area with gridlines */}
        <div className="flex-1 relative h-48">
          {/* Horizontal gridlines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {gridlines.map((_, i) => (
              <div
                key={i}
                className={`border-t border-dashed ${
                  i === gridlines.length - 1
                    ? "border-ink-200"
                    : "border-ink-100"
                }`}
              />
            ))}
          </div>

          {/* Avg reference line */}
          <div
            className="absolute left-0 right-0 border-t border-dashed border-ink-300/70 pointer-events-none"
            style={{ bottom: `${(avg / max) * 100}%` }}
          >
            <span className="absolute -top-2.5 right-0 bg-white px-1.5 text-[9px] font-semibold text-ink-500 rounded">
              avg {formatShort(avg)}
            </span>
          </div>

          {/* Bars */}
          <div className="absolute inset-0 flex items-end justify-between gap-2">
            {data.bars.map((d) => {
              const h = Math.max(6, (d.value / max) * 100);
              const isPeak = d.value === peak.value;
              const isLow = d.value === low.value;
              const aboveAvg = d.value >= avg;
              return (
                <div
                  key={d.label}
                  className="flex-1 h-full flex flex-col items-center justify-end gap-1.5 group cursor-pointer min-w-0 relative"
                >
                  {/* Always-visible value chip on the peak bar */}
                  {isPeak && (
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-ink-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
                      {formatShort(d.value)}
                    </div>
                  )}
                  {/* Hover tooltip for every bar */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-ink-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    {formatShort(d.value)}
                  </div>

                  <div
                    className={`w-full rounded-md transition-all duration-500 ${
                      isPeak
                        ? "bg-ink-900"
                        : isLow
                        ? "bg-ink-200 group-hover:bg-ink-300"
                        : aboveAvg
                        ? "bg-ink-500 group-hover:bg-ink-700"
                        : "bg-ink-300 group-hover:bg-ink-400"
                    }`}
                    style={{ height: `${h}%` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex gap-3 mt-2">
        <div className="w-9" />
        <div className="flex-1 flex justify-between gap-2">
          {data.bars.map((d) => (
            <div
              key={d.label}
              className="flex-1 text-center text-[11px] font-medium text-ink-500 min-w-0 truncate"
            >
              {d.label}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-4 mt-4 pt-3 border-t border-ink-100 text-[11px] text-ink-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-ink-900" />
          Peak
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-ink-500" />
          Above avg
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-ink-300" />
          Below avg
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-px border-t border-dashed border-ink-400" />
          Average
        </span>
      </div>
    </div>
  );
}